import { useId } from 'react';
import StandardSettings from './StandardSettings';
import { AnomalyMetric, BehaviourDetail, BehaviourSettings } from './types';

type Props = {
  detail: BehaviourDetail;
  settings: BehaviourSettings;
  onChange: (value: BehaviourSettings) => void;
  disabled: boolean;
};
const week = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];
function OptionalNumber({
  label,
  value,
  placeholder,
  hint,
  onChange,
  max = 1e9,
  min = 0,
}: {
  label: string;
  value: number | null;
  placeholder: string;
  hint: string;
  onChange: (value: number | null) => void;
  max?: number;
  min?: number;
}) {
  const id = useId();
  return (
    <div className="behaviour-number">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        aria-describedby={`${id}-hint`}
        type="number"
        min={min}
        max={max}
        step="any"
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      />
      <small id={`${id}-hint`} className="behaviour-field-hint">
        <strong>{value === null ? 'Automatisk' : 'Egen værdi'}</strong> · {hint}
      </small>
    </div>
  );
}
export default function BehaviourSettingsForm({ detail, settings, onChange, disabled }: Props) {
  function set<K extends keyof BehaviourSettings>(key: K, value: BehaviourSettings[K]) {
    onChange({ ...settings, [key]: value });
  }
  const metrics: { key: AnomalyMetric; title: string }[] =
    detail.plant.deviceFamily === 'GSM'
      ? [
          { key: 'water', title: 'Vand' },
          { key: 'binder', title: 'Binder' },
        ]
      : [{ key: 'flow', title: 'Niveau' }];
  const included = (metric: AnomalyMetric) =>
    !settings.includedMetrics?.length || settings.includedMetrics.includes(metric);
  return (
    <fieldset disabled={disabled} className="behaviour-settings">
      <p className="behaviour-help">
        Indstillingerne gælder dette anlæg / denne sensor. Tomme felter bruger automatisk beregning.
        Du kan f.eks. kun angive vandets afvigelse og lade resten være tomt. Nul betyder nul — ikke
        automatisk.
      </p>
      <StandardSettings detail={detail} />
      <section className="behaviour-setting-section">
        <h3>Begræns analysen til (valgfrit)</h3>
        <p className="anomaly-muted">
          Ingen valgt = alle målinger undersøges. Vælg kun de målinger, du vil have med, hvis resten
          skal ignoreres.
        </p>
        <div
          className="behaviour-metric-choices"
          role="group"
          aria-label="Målinger som skal med i analysen"
        >
          {metrics.map((metric) => (
            <label key={metric.key} className="behaviour-check">
              <input
                type="checkbox"
                checked={settings.includedMetrics?.includes(metric.key) ?? false}
                onChange={(e) => {
                  const selected = e.target.checked
                    ? [...(settings.includedMetrics ?? []), metric.key]
                    : settings.includedMetrics?.filter((key) => key !== metric.key) ?? [];
                  set('includedMetrics', selected.length ? selected : null);
                }}
              />
              {metric.title}
            </label>
          ))}
        </div>
        <p className="behaviour-inclusion-summary" role="status">
          Undersøges:{' '}
          {metrics
            .filter((metric) => included(metric.key))
            .map((metric) => metric.title)
            .join(', ')}
          {!settings.includedMetrics?.length ? ' (alle · standard)' : ''}.
        </p>
        <p className="anomaly-muted">
          Kun de valgte målinger kan skabe nye afvigelser. Øvrige målinger kan stadig ses som rå
          statistik. Eksisterende hændelser og gemte forventninger bevares.
        </p>
        {!!settings.includedMetrics?.length && (
          <button
            type="button"
            className="anomaly-button"
            onClick={() => set('includedMetrics', null)}
          >
            Undersøg alle målinger
          </button>
        )}
      </section>
      {detail.plant.deviceFamily === 'GSM' ? (
        <>
          <section className="behaviour-setting-section">
            <h3>Hvad skal perioderne sammenlignes med?</h3>
            <label className="behaviour-check">
              <input
                type="checkbox"
                checked={settings.compareOperatingPeriods}
                onChange={(e) => set('compareOperatingPeriods', e.target.checked)}
              />{' '}
              Sammenlign drift med drift og stilstand med stilstand
            </label>
            <label className="behaviour-check">
              <input
                type="checkbox"
                checked={settings.compareWeekdays}
                onChange={(e) => set('compareWeekdays', e.target.checked)}
              />{' '}
              Tag højde for ugedage
            </label>
            <p className="anomaly-muted">
              Mindst 7 tilsvarende driftsperioder eller 4 tilsvarende ugedage kræves. Ellers bruges
              den nuværende standard. Målingens eget forbrug bruges aldrig til at afgøre, om
              anlægget kører.
            </p>
          </section>
          <section className="behaviour-setting-section">
            <h3>Hvordan ved vi, om anlægget er i brug?</h3>
            <label className="behaviour-check">
              <input
                type="checkbox"
                checked={settings.usePumpRuntime}
                onChange={(e) => set('usePumpRuntime', e.target.checked)}
              />{' '}
              Brug målt pumpetid, når den er tilgængelig
            </label>
            <p className="anomaly-muted">
              Gyldig pumpetid for {detail.pumpIntervals} af {detail.totalIntervals} seneste
              målepunkter. Manglende eller nulstillet tæller regnes som ukendt, ikke stilstand.
            </p>
            <label className="behaviour-check">
              <input
                type="checkbox"
                checked={settings.schedule !== null}
                onChange={(e) =>
                  set(
                    'schedule',
                    e.target.checked
                      ? { weekdays: [1, 2, 3, 4, 5], startHour: 7, endHour: 17 }
                      : null
                  )
                }
              />{' '}
              Tilføj en forventet driftsplan
            </label>
            {settings.schedule && (
              <>
                <div className="behaviour-days" role="group" aria-label="Driftsdage">
                  {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                    <label key={day}>
                      <input
                        type="checkbox"
                        checked={settings.schedule!.weekdays.includes(day)}
                        onChange={(e) =>
                          set('schedule', {
                            ...settings.schedule!,
                            weekdays: e.target.checked
                              ? [...settings.schedule!.weekdays, day]
                              : settings.schedule!.weekdays.filter((d) => d !== day),
                          })
                        }
                      />
                      {week[day]}
                    </label>
                  ))}
                </div>
                <div className="behaviour-hour-fields">
                  <label>
                    Fra kl.
                    <select
                      aria-label="Driftsplan fra time"
                      value={settings.schedule.startHour}
                      onChange={(e) =>
                        set('schedule', {
                          ...settings.schedule!,
                          startHour: Number(e.target.value),
                        })
                      }
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>
                          {String(h).padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Til kl.
                    <select
                      aria-label="Driftsplan til time"
                      value={settings.schedule.endHour}
                      onChange={(e) =>
                        set('schedule', { ...settings.schedule!, endHour: Number(e.target.value) })
                      }
                    >
                      {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => (
                        <option key={h} value={h}>
                          {String(h).padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <p className="anomaly-muted">
                  Dansk tid. Dagvagt inden for samme døgn. Planen bruges, hvis et pålideligt
                  pumpesignal mangler. Et døgn med planlagt drift vurderes som en driftsperiode;
                  forbruget måles stadig i liter pr. døgn.
                </p>
              </>
            )}
          </section>
          {(['water', 'binder'] as const).map((metric) => (
            <fieldset
              key={metric}
              disabled={!included(metric)}
              className="behaviour-setting-section"
            >
              <h3>{metric === 'water' ? 'Vand' : 'Binder'} · forventet forbrug</h3>
              {!included(metric) && (
                <p className="anomaly-muted">
                  Ikke valgt til analyse. Forventningerne bevares til senere.
                </p>
              )}
              <div className="behaviour-number-grid">
                <OptionalNumber
                  label="Generelt (liter/døgn)"
                  value={settings[metric].expected}
                  placeholder="Beregnes fra historik"
                  hint="Tomt felt: medianen af tidligere forbrug."
                  onChange={(expected) => set(metric, { ...settings[metric], expected })}
                />
                <OptionalNumber
                  label="Når anlægget er i brug (liter/døgn)"
                  value={settings[metric].operating}
                  placeholder="Generel forventning / historik"
                  hint="Tomt felt: din generelle forventning, ellers historik."
                  onChange={(operating) => set(metric, { ...settings[metric], operating })}
                />
                <OptionalNumber
                  label="Når anlægget står stille (liter/døgn)"
                  value={settings[metric].idle}
                  placeholder="Generel forventning / historik"
                  hint="Tomt felt: din generelle forventning, ellers historik."
                  onChange={(idle) => set(metric, { ...settings[metric], idle })}
                />
                <OptionalNumber
                  label="Acceptabel afvigelse ± %"
                  value={settings[metric].deviationPercent}
                  max={1000}
                  placeholder="Automatisk tolerance"
                  hint={
                    settings[metric].deviationAbsolute !== null
                      ? 'Tomt felt: kun den angivne litermargin bruges.'
                      : 'Begge marginfelter tomme: største af 75 %, variation og mindstemargin.'
                  }
                  onChange={(deviationPercent) =>
                    set(metric, { ...settings[metric], deviationPercent })
                  }
                />
                <OptionalNumber
                  label="Acceptabel afvigelse ± liter/døgn"
                  value={settings[metric].deviationAbsolute}
                  placeholder="Automatisk tolerance"
                  hint={
                    settings[metric].deviationPercent !== null
                      ? 'Tomt felt: kun den angivne procentmargin bruges.'
                      : `Begge marginfelter tomme: automatisk margin, mindst ±${
                          metric === 'water' ? '500' : '5'
                        } liter/døgn.`
                  }
                  onChange={(deviationAbsolute) =>
                    set(metric, { ...settings[metric], deviationAbsolute })
                  }
                />
              </div>
              <p className="anomaly-muted">
                Angiver du både procent og liter, bruges den største margin. En driftsbestemt
                forventning har forrang for den generelle. Er begge tomme, bruges historikken.
              </p>
            </fieldset>
          ))}
        </>
      ) : (
        <section className="behaviour-setting-section">
          <h3>Acceptabel ændring i niveau</h3>
          <div className="behaviour-number-grid">
            <OptionalNumber
              label="Flag spring fra (cm)"
              min={0.1}
              max={10000}
              value={settings.flow.jumpCm}
              placeholder="Standard: 20 cm"
              hint="Tomt felt: spring fra 20 cm."
              onChange={(jumpCm) => set('flow', { ...settings.flow, jumpCm })}
            />
            <OptionalNumber
              label="Inden for (hele minutter)"
              min={1}
              max={1440}
              value={settings.flow.windowMinutes}
              placeholder="Standard: 120 min."
              hint="Tomt felt: højst 120 minutter mellem målinger."
              onChange={(windowMinutes) => set('flow', { ...settings.flow, windowMinutes })}
            />
          </div>
          <p className="anomaly-muted">
            Flow-sensoren måler niveau i cm. Et pålideligt driftssignal er ikke tilsluttet denne
            måling, så der udledes ikke et vand- eller binderforbrug fra niveauet.
          </p>
        </section>
      )}
      <button className="anomaly-button" type="button" onClick={() => onChange(detail.defaults)}>
        Brug standardindstillinger
      </button>
    </fieldset>
  );
}
